/**
 * ReportRichText — tiny, safe subset-of-Markdown renderer for weather
 * and civic report bodies.
 *
 * Handles just enough syntax to give a rich reading experience without
 * pulling in a heavyweight markdown dependency:
 *
 *   **bold**              → <strong>
 *   _italic_ / *italic*   → <em>
 *   [text](/place/pala)   → <Link to="/place/pala">text</Link>
 *   [text](https://…)     → <a href target=_blank rel=noreferrer>
 *   - item                → bulleted list
 *   double newline        → paragraph break
 *   single newline        → <br/>
 *
 * Deliberately NOT supported: headings, tables, images, code fences,
 * raw HTML. Anything unrecognised is rendered as plain text so the
 * report never breaks.
 */
import { Link } from "react-router-dom";

const _LINK    = /\[([^\]]+)\]\(([^)]+)\)/;
const _BOLD    = /\*\*([^*]+)\*\*/;
const _ITAL_1  = /_([^_]+)_/;
const _ITAL_2  = /\*([^*]+)\*/;

/**
 * Render an inline string with bold / italic / link support.
 * Recursion is safe because each pass strips the matched span and the
 * remaining left/right chunks are processed independently.
 */
function renderInline(text, keyPrefix = "i") {
  if (!text) return null;
  // Pick the earliest match across all supported tokens.
  const candidates = [];
  const push = (re, kind) => {
    const m = re.exec(text);
    if (m) candidates.push({ index: m.index, len: m[0].length, kind, groups: m });
  };
  push(_LINK, "link");
  push(_BOLD, "bold");
  push(_ITAL_1, "ital");
  push(_ITAL_2, "ital");
  if (candidates.length === 0) return text;

  candidates.sort((a, b) => a.index - b.index);
  const first = candidates[0];
  const before = text.slice(0, first.index);
  const after  = text.slice(first.index + first.len);
  let matchEl = null;

  if (first.kind === "link") {
    const [, label, href] = first.groups;
    if (/^https?:\/\//.test(href)) {
      matchEl = (
        <a
          key={`${keyPrefix}-a`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-1 underline-offset-2 hover:decoration-2"
          style={{ color: "var(--wofy-primary)" }}
        >
          {label}
        </a>
      );
    } else {
      matchEl = (
        <Link
          key={`${keyPrefix}-l`}
          to={href}
          className="underline decoration-1 underline-offset-2 hover:decoration-2"
          style={{ color: "var(--wofy-primary)" }}
        >
          {label}
        </Link>
      );
    }
  } else if (first.kind === "bold") {
    matchEl = <strong key={`${keyPrefix}-b`}>{renderInline(first.groups[1], `${keyPrefix}-b`)}</strong>;
  } else if (first.kind === "ital") {
    matchEl = <em key={`${keyPrefix}-i`}>{renderInline(first.groups[1], `${keyPrefix}-i`)}</em>;
  }

  return [
    before ? renderInline(before, `${keyPrefix}-l`) : null,
    matchEl,
    after ? renderInline(after, `${keyPrefix}-r`) : null,
  ];
}

/** Split into blocks (paragraphs + bulleted lists) and render each. */
export default function ReportRichText({ text, className = "" }) {
  if (!text) return null;
  const paragraphs = String(text).split(/\n{2,}/); // blank line = block break
  return (
    <div className={`space-y-3 leading-relaxed ${className}`}>
      {paragraphs.map((raw, i) => {
        const lines = raw.split("\n");
        // Bullet list detection: every non-empty line begins with "- ".
        const nonEmpty = lines.filter((l) => l.trim());
        const isList = nonEmpty.length > 0 && nonEmpty.every((l) => /^\s*-\s+/.test(l));
        if (isList) {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1">
              {nonEmpty.map((l, j) => (
                <li key={j}>{renderInline(l.replace(/^\s*-\s+/, ""), `p${i}-b${j}`)}</li>
              ))}
            </ul>
          );
        }
        // Regular paragraph — single newlines become soft breaks.
        return (
          <p key={i}>
            {lines.map((line, j) => (
              <span key={j}>
                {renderInline(line, `p${i}-l${j}`)}
                {j < lines.length - 1 ? <br /> : null}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
